import React from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { chartColors, useChartTheme } from "./ChartTheme";
import DashboardCard from "./DashboardCard";
import CustomNoData from "../../../components/CustomNoData";

const CUSTOM_FONT_GOLD = "#D3AF37";
const WEEKEND_LABELS = new Set(["sat", "saturday", "sun", "sunday"]);

const parseDateValue = (value) => {
  if (!value) return null;

  const dateText = String(value).trim();
  const hasFullDate = /^\d{4}-\d{2}-\d{2}/.test(dateText);
  const hasYear = /\b\d{4}\b/.test(dateText);

  if (!hasFullDate && !hasYear) return null;

  const normalizedDateText = hasFullDate ? `${dateText.slice(0, 10)}T00:00:00` : dateText;
  const date = new Date(normalizedDateText);

  return Number.isFinite(date.valueOf()) ? date : null;
};

const isWeekendDailyItem = (item = {}) => {
  const date = parseDateValue(item.selectedDate || item.rawDate || item.date);

  if (date) {
    return date.getDay() === 0 || date.getDay() === 6;
  }

  const labelText = String(item.weekday || item.day || item.date || "").toLowerCase();
  const labelParts = labelText.split(/[^a-z]+/).filter(Boolean);

  return labelParts.some((part) => WEEKEND_LABELS.has(part));
};

const formatTooltipLabel = (label, payload = []) => {
  const item = payload[0]?.payload || {};
  const date = parseDateValue(item.selectedDate || item.rawDate);

  if (!date) return label;

  const day = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date);

  return `${label} (${day})`;
};

const TrendChart = ({ data = [] }) => {
  const chartTheme = useChartTheme();
  const renderXAxisTick = ({ x, y, payload }) => {
    const item = data[payload.index] || data.find((dataItem) => dataItem.date === payload.value) || { date: payload.value };
    const fill = isWeekendDailyItem(item) ? CUSTOM_FONT_GOLD : chartTheme.axis;

    return (
      <text x={x} y={y} dy={16} textAnchor="middle" fill={fill} fontSize={11}>
        {payload.value}
      </text>
    );
  };

  return (
    <DashboardCard className="p-3">
      <header className="mb-2 flex flex-col">
        <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-customInk dark:text-customHeading">Betting Amount & Net Profit Trend</h2>
        <span className="text-[11px] text-customSecondaryText dark:text-customMuted">
          Highlighted orange columns represent weekends
        </span>
        </div>
        <div className="flex justify-between">
          <p className="mt-2 text-[11px] text-customSecondaryText dark:text-customMuted" style={{color: chartColors.blue}}>Total Bet Amount (M Ks)</p>
          <p className="mt-2 text-[11px] text-customSecondaryText dark:text-customMuted" style={{color: chartColors.green}}>Net Profit (M Ks)</p>
        </div> 
        {/* <div className="flex overflow-hidden rounded-md border border-slate-200 text-[11px] dark:border-customBorder">
          {["Daily", "Weekly", "Monthly"].map((tab) => (
            <button
              type="button"
              key={tab}
              className={`h-7 px-3 font-semibold ${
                tab === "Daily"
                  ? "bg-customAccentBlue text-white"
                  : "bg-white text-customPrimaryText dark:bg-customSurface dark:text-customMuted"
              }`}
            >
              {tab}
            </button>
          ))}
        </div> */}
      </header>
      <div className="h-[220px]">
        {data.length === 0 ? (
          <CustomNoData title="No trend data" description="No betting trend data found for the selected date range." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: -18 }}>
              <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={renderXAxisTick} axisLine={false} tickLine={false} />
              <YAxis
                yAxisId="amount"
                tick={{ fill: chartColors.blue, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="profit"
                orientation="right"
                tick={{ fill: chartColors.green, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                labelFormatter={formatTooltipLabel}
                contentStyle={{ background: chartTheme.tooltipBackground, borderColor: chartTheme.grid, color: chartTheme.tooltipText }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="amount" dataKey="amount" name="Total Bet Amount (M Ks)" fill={chartColors.blue} radius={[3, 3, 0, 0]} barSize={30}>
                {data.map((item) => (
                  <Cell key={item.selectedDate || item.date} fill={isWeekendDailyItem(item) ? CUSTOM_FONT_GOLD : chartColors.blue} />
                ))}
              </Bar>
              <Line yAxisId="profit" type="monotone" dataKey="profit" name="Net Profit (M Ks)" stroke={chartColors.green} strokeWidth={2} dot={{ r: 3, fill: chartColors.green, stroke: "#ffffff" }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </DashboardCard>
  );
};

export default TrendChart;
